import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useProjects } from '../../hooks/useProjects';
import { useCustomers } from '../../hooks/useCustomers';
import { useUsers } from '../../hooks/useUsers';
import Modal from '../common/Modal';
import SearchableSelect from '../common/SearchableSelect';
import { 
  BoltIcon, 
  DocumentPlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface BulkTaskCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateTasks: (tasks: BulkTaskData[]) => Promise<void>;
}

export interface BulkTaskData {
  title: string;
  description?: string;
  dueDate?: Date;
  estimatedHours?: number;
  actualHours?: number;
  projectId?: string;
  customerId?: string;
  assigneeId?: string;
  assigneeName?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'todo' | 'in_progress' | 'completed' | 'cancelled';
}

const BulkTaskCreator: React.FC<BulkTaskCreatorProps> = ({
  isOpen,
  onClose,
  onCreateTasks
}) => {
  const { t } = useLanguage();
  const { projects } = useProjects();
  const { customers } = useCustomers();
  const { users } = useUsers();

  const [taskText, setTaskText] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [defaultPriority, setDefaultPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [defaultStatus, setDefaultStatus] = useState<'todo' | 'in_progress' | 'completed' | 'cancelled'>('todo');
  const [isCreating, setIsCreating] = useState(false);

  const taskLines = taskText.split('\n').filter(line => line.trim() !== '');
  const taskCount = taskLines.length;

  const parseTaskLine = (line: string) => {
    let title = line.trim();
    let description: string | undefined = undefined;
    let dueDate: Date | undefined = undefined;
    let estimatedHours: number | undefined = undefined;
    let actualHours: number | undefined = undefined;
    let assigneeId = selectedAssignee;
    let assigneeName = selectedAssignee ? users.find(u => u.id === selectedAssignee)?.displayName : undefined;
    let priority = defaultPriority;

    // Ha van pipe (|) szeparátor, akkor oszlopokra bontjuk
    // Formátum: határidő | leírás | felelős@email | perc
    if (line.includes('|')) {
      const parts = line.split('|').map(p => p.trim());
      
      // 1. oszlop: határidő (opcionális)
      if (parts[0]) {
        const dueDateStr = parts[0].trim();
        if (dueDateStr) {
          // Próbáljuk meg parse-olni a dátumot (YYYY-MM-DD vagy YYYY.MM.DD vagy MM.DD vagy MM/DD)
          const parsedDate = parseDateString(dueDateStr);
          if (parsedDate) {
            dueDate = parsedDate;
          }
        }
      }
      
      // 2. oszlop: leírás (kötelező, ez lesz a title)
      if (parts[1]) {
        title = parts[1].trim();
      }
      
      // 3. oszlop: felelős email (opcionális)
      if (parts[2]) {
        const emailStr = parts[2].trim();
        if (emailStr) {
          const foundUser = users.find(u => 
            u.email?.toLowerCase() === emailStr.toLowerCase() ||
            u.displayName?.toLowerCase().includes(emailStr.toLowerCase())
          );
          
          if (foundUser) {
            assigneeId = foundUser.id;
            assigneeName = foundUser.displayName;
          }
        }
      }
      
      // 4. oszlop: becsült perc (opcionális)
      if (parts[3]) {
        const minutesStr = parts[3].trim();
        const minutes = parseInt(minutesStr);
        if (!isNaN(minutes) && minutes > 0) {
          estimatedHours = minutes / 60; // Percből órába konvertálás
        }
      }
      
      // 5. oszlop: tényleges perc (opcionális)
      if (parts[4]) {
        const actualMinutesStr = parts[4].trim();
        const actualMinutes = parseInt(actualMinutesStr);
        if (!isNaN(actualMinutes) && actualMinutes > 0) {
          actualHours = actualMinutes / 60; // Percből órába konvertálás
        }
      }
    } else {
      // Régi formátum támogatása (@email és #priority a sor végén)
      // Parse priority (#low, #medium, #high, #urgent)
      const priorityMatch = title.match(/#(low|medium|high|urgent)\s*$/i);
      if (priorityMatch) {
        priority = priorityMatch[1].toLowerCase() as 'low' | 'medium' | 'high' | 'urgent';
        title = title.replace(priorityMatch[0], '').trim();
      }

      // Parse assignee (@email or @name)
      const assigneeMatch = title.match(/@([^\s#]+)\s*$/);
      if (assigneeMatch) {
        const identifier = assigneeMatch[1];
        // Keresés email vagy név alapján
        const foundUser = users.find(u => 
          u.email?.toLowerCase() === identifier.toLowerCase() ||
          u.displayName?.toLowerCase().includes(identifier.toLowerCase())
        );
        
        if (foundUser) {
          assigneeId = foundUser.id;
          assigneeName = foundUser.displayName;
        }
        
        title = title.replace(assigneeMatch[0], '').trim();
      }
    }

    return {
      title,
      description,
      dueDate,
      estimatedHours,
      actualHours,
      assigneeId,
      assigneeName,
      priority
    };
  };

  // Dátum parse segédfüggvény
  const parseDateString = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined;
    
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // YYYY-MM-DD vagy YYYY.MM.DD formátum
    const fullDateMatch = dateStr.match(/^(\d{4})[-.](\d{1,2})[-.](\d{1,2})$/);
    if (fullDateMatch) {
      const [, year, month, day] = fullDateMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    
    // MM-DD vagy MM.DD vagy MM/DD formátum (idei év)
    const shortDateMatch = dateStr.match(/^(\d{1,2})[-./](\d{1,2})$/);
    if (shortDateMatch) {
      const [, month, day] = shortDateMatch;
      return new Date(currentYear, parseInt(month) - 1, parseInt(day));
    }
    
    return undefined;
  };

  const handleCreate = async () => {
    if (taskLines.length === 0) {
      toast.error('Adj meg legalább egy feladatot!');
      return;
    }

    setIsCreating(true);
    try {
      const tasks: BulkTaskData[] = taskLines.map(line => {
        const parsed = parseTaskLine(line);
        return {
          title: parsed.title,
          description: parsed.description,
          dueDate: parsed.dueDate,
          estimatedHours: parsed.estimatedHours,
          actualHours: parsed.actualHours,
          projectId: selectedProject || undefined,
          customerId: selectedCustomer || undefined,
          assigneeId: parsed.assigneeId || undefined,
          assigneeName: parsed.assigneeName || undefined,
          priority: parsed.priority,
          status: defaultStatus
        };
      });

      await onCreateTasks(tasks);
      
      toast.success(`${tasks.length} feladat sikeresen létrehozva!`);
      handleClose();
    } catch (error) {
      console.error('Error creating bulk tasks:', error);
      toast.error('Hiba történt a feladatok létrehozásakor');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setTaskText('');
    setSelectedProject('');
    setSelectedCustomer('');
    setSelectedAssignee('');
    setDefaultPriority('medium');
    setDefaultStatus('todo');
    onClose();
  };

  const projectOptions = [
    { value: '', label: 'Nincs projekt', icon: '📋' },
    ...projects.map(project => ({
      value: project.id,
      label: project.name,
      icon: '📁'
    }))
  ];

  const customerOptions = [
    { value: '', label: 'Nincs ügyfél', icon: '👤' },
    ...customers.map(customer => ({
      value: customer.id,
      label: customer.companyName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Névtelen',
      icon: '🏢'
    }))
  ];

  const assigneeOptions = [
    { value: '', label: 'Nincs felelős', icon: '👤' },
    ...users.map(user => ({
      value: user.id,
      label: user.displayName ? `${user.displayName} (${user.email})` : user.email,
      icon: '👨‍💼'
    }))
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="⚡ Gyors Feladat Létrehozás"
      size="5xl"
      zIndex={50}
    >

      <div className="space-y-6">
        {/* Instrukciók */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <ExclamationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Használat
              </h3>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• Minden sor egy új feladatot jelent</li>
                <li>• Az üres sorok automatikusan kihagyásra kerülnek</li>
                <li>• <strong>Egyszerű formátum:</strong> Csak a feladat címe, opcionálisan <code className="bg-blue-200 dark:bg-blue-800 px-1 rounded">@email</code> és <code className="bg-blue-200 dark:bg-blue-800 px-1 rounded">#prioritás</code></li>
                <li>• <strong>Részletes formátum (pipe szeparált):</strong></li>
                <li className="ml-4">
                  <code className="bg-blue-200 dark:bg-blue-800 px-1 rounded text-xs">határidő | leírás | felelős@email | becsült_perc | tényleges_perc</code>
                </li>
                <li className="ml-4 text-xs">
                  - Határidő: YYYY-MM-DD vagy MM-DD formátum<br/>
                  - Leírás: A feladat címe (kötelező)<br/>
                  - Felelős: Email cím (opcionális)<br/>
                  - Becsült perc: Becsült idő percben (opcionális)<br/>
                  - Tényleges perc: Tényleges idő percben (opcionális)
                </li>
                <li className="text-xs italic mt-2">Példák:</li>
                <li className="text-xs ml-4">• "Weboldal design @john@example.com #high"</li>
                <li className="text-xs ml-4">• "2024-12-31 | API fejlesztés | dev@company.com | 120 | 150"</li>
                <li className="text-xs ml-4">• "12-25 | Karácsonyi kampány | | 60 | 75"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Projekt, Ügyfél és Felelős választó */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Projekt (opcionális)
            </label>
            <SearchableSelect
              options={projectOptions}
              value={selectedProject}
              onChange={setSelectedProject}
              placeholder="Válassz projektet..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ügyfél (opcionális)
            </label>
            <SearchableSelect
              options={customerOptions}
              value={selectedCustomer}
              onChange={setSelectedCustomer}
              placeholder="Válassz ügyfelet..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Felelős (opcionális)
            </label>
            <SearchableSelect
              options={assigneeOptions}
              value={selectedAssignee}
              onChange={setSelectedAssignee}
              placeholder="Válassz felelőst..."
            />
          </div>
        </div>

        {/* Alapértelmezett beállítások */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alapértelmezett prioritás
            </label>
            <select
              value={defaultPriority}
              onChange={(e) => setDefaultPriority(e.target.value as any)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 transition-all"
            >
              <option value="low">🟢 Alacsony</option>
              <option value="medium">🟡 Közepes</option>
              <option value="high">🟠 Magas</option>
              <option value="urgent">🔴 Sürgős</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alapértelmezett státusz
            </label>
            <select
              value={defaultStatus}
              onChange={(e) => setDefaultStatus(e.target.value as any)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 transition-all"
            >
              <option value="todo">📋 Tennivaló</option>
              <option value="in_progress">🔄 Folyamatban</option>
              <option value="completed">✅ Kész</option>
              <option value="cancelled">❌ Törölve</option>
            </select>
          </div>
        </div>

        {/* Feladat szöveg beviteli mező */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Feladatok (soronként egy)
            </label>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {taskCount} feladat
            </span>
          </div>
          <textarea
            value={taskText}
            onChange={(e) => setTaskText(e.target.value)}
            placeholder="Egyszerű formátum:&#10;Weboldal design elkészítése @john@example.com #high&#10;Adatbázis migrálás @jane #medium&#10;&#10;Részletes formátum (pipe szeparált):&#10;2024-12-31 | API fejlesztés | dev@company.com | 120 | 150&#10;12-25 | Karácsonyi kampány | marketing@company.com | 60 | 75&#10;01-15 | Tesztelés | | 90 | 105"
            rows={12}
            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 transition-all font-mono text-sm"
            disabled={isCreating}
          />
        </div>

        {/* Előnézet */}
        {taskCount > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-3">
              <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Előnézet ({taskCount} feladat)
              </h3>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {taskLines.slice(0, 10).map((line, index) => {
                const parsed = parseTaskLine(line);
                const priorityColors = {
                  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
                  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
                  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
                  urgent: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                };
                
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-2 text-sm bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600"
                  >
                    <span className="text-xs font-mono text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5">
                      {index + 1}.
                    </span>
                    <div className="flex-1 space-y-1">
                      <div className="text-gray-900 dark:text-white font-medium">
                        {parsed.title}
                      </div>
                      {parsed.description && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {parsed.description}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${priorityColors[parsed.priority]}`}>
                          {parsed.priority === 'low' && '🟢 Alacsony'}
                          {parsed.priority === 'medium' && '🟡 Közepes'}
                          {parsed.priority === 'high' && '🟠 Magas'}
                          {parsed.priority === 'urgent' && '🔴 Sürgős'}
                        </span>
                        {parsed.assigneeName && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                            👤 {parsed.assigneeName}
                          </span>
                        )}
                        {parsed.dueDate && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
                            📅 {parsed.dueDate.toLocaleDateString('hu-HU')}
                          </span>
                        )}
                        {parsed.estimatedHours && (
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 rounded-full">
                            ⏱️ Becsült: {Math.round(parsed.estimatedHours * 60)} perc
                          </span>
                        )}
                        {parsed.actualHours && (
                          <span className="px-2 py-0.5 bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 rounded-full">
                            ✅ Tényleges: {Math.round(parsed.actualHours * 60)} perc
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {taskCount > 10 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                  ... és még {taskCount - 10} feladat
                </p>
              )}
            </div>

            {/* Összesített idők */}
            {(() => {
              const totals = taskLines.reduce((acc, line) => {
                const parsed = parseTaskLine(line);
                return {
                  estimated: acc.estimated + (parsed.estimatedHours || 0),
                  actual: acc.actual + (parsed.actualHours || 0)
                };
              }, { estimated: 0, actual: 0 });

              if (totals.estimated === 0 && totals.actual === 0) return null;

              return (
                <div className="mt-4 pt-3 border-t-2 border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Összesítés:
                  </span>
                  {totals.estimated > 0 && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 text-sm font-medium">
                      ⏱️ Becsült: {Math.round(totals.estimated * 60)} perc ({totals.estimated.toFixed(2)} h)
                    </span>
                  )}
                  {totals.actual > 0 && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 text-sm font-medium">
                      ✅ Tényleges: {Math.round(totals.actual * 60)} perc ({totals.actual.toFixed(2)} h)
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-6 mt-6 border-t-2 border-gray-100 dark:border-gray-700">
        <button
          onClick={handleClose}
          disabled={isCreating}
          className="px-5 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors disabled:opacity-50"
        >
          Mégse
        </button>
        <button
          onClick={handleCreate}
          disabled={isCreating || taskCount === 0}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <DocumentPlusIcon className="h-5 w-5" />
          <span>
            {isCreating ? 'Létrehozás...' : `${taskCount} Feladat Létrehozása`}
          </span>
        </button>
      </div>
    </Modal>
  );
};

export default BulkTaskCreator;
